// the Google Maps script is loaded once per page, whatever opens the map
let mapsScriptPromise = null;
function loadMapsScript(apiKey) {
    if (window.google?.maps?.Map) {
        return Promise.resolve();
    }
    if (!mapsScriptPromise) {
        mapsScriptPromise = new Promise((resolve, reject) => {
            const callbackName = `initMap_${Math.random().toString(36).slice(2)}`;
            window[callbackName] = () => resolve();
            const tag = document.createElement("script");
            tag.id = "gmapjs";
            tag.async = true;
            tag.src = "https://maps.google.com/maps/api/js?key=" + encodeURIComponent(apiKey ?? "") + "&callback=" + callbackName;
            tag.onerror = () => {
                mapsScriptPromise = null;
                reject(new Error("Google Maps could not be loaded"));
            };
            document.body.appendChild(tag);
        });
    }
    return mapsScriptPromise;
}

const sameCenter = (a, b) => a && b
    && Math.abs(Number(a.lat) - Number(b.lat)) < 1e-7
    && Math.abs(Number(a.lng) - Number(b.lng)) < 1e-7;

// Phone: the bottom sheet (.point-sheet) floats over the lower part of the full-screen map and the search field over its
// top. The point the map shows belongs in the visible band between them, not in the middle of the screen: the centre
// is moved down by half the difference (Web Mercator maths, so it is exact at any zoom and needs no loaded map).
const SHEET_TOP_INSET = 72;
const tilePx = 256;
function shiftCenterDown(center, pixels, zoom) {
    const lat = Number(center.lat);
    const lng = Number(center.lng);
    if (!pixels || !Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(Number(zoom))) {
        return {lat, lng};
    }
    const world = tilePx * 2 ** Number(zoom);
    const sin = Math.sin(lat * Math.PI / 180);
    let y = 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
    y += pixels / world;
    const n = Math.PI - 2 * Math.PI * y;
    return {lat: 180 / Math.PI * Math.atan(Math.sinh(n)), lng};
}

let map = {
    template: `
        <div ref="map" class="point-map-canvas" style="width: 100%; height: 100%; min-height: 220px;"></div>
    `,
    props: {
        apiKey: String,
        center: Object,
        zoom: Number,
        points: Array,
        selectedId: {type: [String, Number], default: null},
    },
    data() {
        return {
            map: null,
            markers: [],
            markerById: {},
            renderSeq: 0,
            appliedCenter: null,
        };
    },
    watch: {
        // the parent passes a new object on every render: only a real change
        // moves the map (a user pan is never snapped back)
        center(value) {
            if (!this.map || sameCenter(value, this.appliedCenter)) return;
            this.appliedCenter = {...value};
            try { this.map.panTo(this.visibleCenter(value, this.zoom ?? this.map.getZoom())); } catch (error) { console.error("map center", error); }
        },
        zoom(value) {
            if (!this.map || this.map.getZoom() === value) return;
            try { this.map.setZoom(value); } catch (error) { console.error("map zoom", error); }
        },
        points() {
            this.setPoints();
        },
        selectedId() {
            this.highlightSelected();
        },
    },
    emits: ["selected", "idle"],
    mounted() {
        loadMapsScript(this.apiKey).then(() => this.loaded()).catch((error) => console.error(error));
    },
    beforeUnmount() {
        this.markers.forEach((marker) => marker.setMap(null));
    },
    methods: {
        // how far down the map centre has to be, so the point is in the middle of what the sheet leaves visible
        sheetShift() {
            const sheet = this.$el?.closest?.(".point-modal-box")?.querySelector(".point-sheet");
            if (!sheet || !window.matchMedia("(max-width: 47.999em)").matches) return 0;
            return Math.max(0, Math.round((sheet.offsetHeight - SHEET_TOP_INSET) / 2));
        },
        visibleCenter(center, zoom) {
            return shiftCenterDown(center, this.sheetShift(), zoom);
        },
        loaded() {
            if (!this.$refs.map) return;
            this.appliedCenter = {...this.center};
            this.map = new google.maps.Map(this.$refs.map, {
                zoom: this.zoom,
                center: this.visibleCenter(this.center, this.zoom),
                mapTypeId: google.maps.MapTypeId.ROADMAP,
                clickableIcons: false,
            });
            // the map stopped moving (pan / zoom by the user or by us)
            this.map.addListener("idle", () => {
                const center = this.map.getCenter();
                const bounds = this.map.getBounds();
                if (!center || !bounds) return;
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                this.$emit("idle", {
                    lat: center.lat(),
                    lng: center.lng(),
                    zoom: this.map.getZoom(),
                    bounds: {north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng()},
                });
            });
            this.setPoints();
        },
        setPoints() {
            if (!this.map) return;
            // a newer list replaces this one: late icon loads of an older list
            // must not add markers (no duplicates)
            const seq = ++this.renderSeq;
            this.markers.forEach((marker) => marker.setMap(null));
            this.markers = [];
            this.markerById = {};
            const maxSize = 34;
            const normalizeIcon = (icon) => new Promise((resolve) => {
                const url = typeof icon === "string" ? icon : icon?.url ?? null;
                if (!url) {
                    resolve(undefined); // default marker
                    return;
                }
                const image = new Image();
                image.onload = () => {
                    const width = image.naturalWidth || maxSize;
                    const height = image.naturalHeight || maxSize;
                    const ratio = Math.min(maxSize / width, maxSize / height);
                    resolve({url, width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio))});
                };
                // icon not available: the default Google pin instead of a broken image
                image.onerror = () => resolve(undefined);
                image.src = url;
            });
            const points = this.points ?? [];
            Promise.all(points.map((point) => normalizeIcon(point.icon))).then((icons) => {
                if (seq !== this.renderSeq || !this.map) return;
                points.forEach((point, i) => {
                    const marker = new google.maps.Marker({
                        map: this.map,
                        position: new google.maps.LatLng(point.geolat, point.geolon),
                        title: point.name ?? "",
                    });
                    marker.__icon = icons[i];
                    marker.addListener("click", () => this.$emit("selected", point));
                    this.markers.push(marker);
                    this.markerById[point.pointId] = marker;
                });
                this.highlightSelected();
            });
        },
        iconFor(marker, selected) {
            const base = marker.__icon;
            if (!base) {
                return undefined;
            }
            const scale = selected ? 1.45 : 1;
            const width = Math.round(base.width * scale);
            const height = Math.round(base.height * scale);
            return {
                url: base.url,
                scaledSize: new google.maps.Size(width, height),
                anchor: new google.maps.Point(width / 2, height / 2),
            };
        },
        // the selected point stands out on the map (bigger, on top)
        highlightSelected() {
            if (!this.map) return;
            for (const id in this.markerById) {
                const marker = this.markerById[id];
                const selected = String(id) === String(this.selectedId);
                marker.setIcon(this.iconFor(marker, selected));
                marker.setZIndex(selected ? 1000 : undefined);
                marker.setAnimation(selected ? google.maps.Animation.DROP : null);
            }
        },
    },
};
export default map;
